import React, { useState, useEffect } from 'react';
import { analyticsAPI, promotionsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Eye, MousePointer, Zap, TrendingUp, 
  Calendar, ArrowUp, ArrowDown
} from 'lucide-react';
import { 
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { toast } from 'sonner';

const Analytics = () => {
  const [overview, setOverview] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [promoAnalytics, setPromoAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, promosRes] = await Promise.all([
        analyticsAPI.getOverview(),
        promotionsAPI.getAll()
      ]);
      setOverview(overviewRes.data);
      setPromotions(promosRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoAnalytics = async (promoId) => {
    try {
      const response = await analyticsAPI.getPromotion(promoId);
      setPromoAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load promotion analytics');
    }
  };

  const handlePromoSelect = (promoId) => {
    setSelectedPromo(promoId);
    if (promoId) {
      fetchPromoAnalytics(promoId);
    } else {
      setPromoAnalytics(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = [
    { 
      label: 'Total Views', 
      value: overview?.total_views || 0, 
      icon: Eye, 
      color: 'mint',
      trend: null
    },
    { 
      label: 'Total Clicks', 
      value: overview?.total_clicks || 0, 
      icon: MousePointer, 
      color: 'violet',
      trend: null
    },
    { 
      label: 'Average CTR', 
      value: `${overview?.avg_ctr || 0}%`, 
      icon: TrendingUp, 
      color: 'amber',
      trend: overview?.avg_ctr > 2 ? 'up' : 'down'
    },
    { 
      label: 'Credits Spent', 
      value: overview?.total_spent || 0, 
      icon: Zap, 
      color: 'mint',
      trend: null
    },
  ];

  const CHART_COLORS = ['#00E599', '#7C3AED', '#F59E0B', '#3B82F6'];

  // Prepare pie chart data
  const pieData = promotions.map((promo, i) => ({
    name: promo.title.slice(0, 20),
    value: promo.views,
    color: CHART_COLORS[i % CHART_COLORS.length]
  })).filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white">Analytics</h1>
          <p className="text-gray-400">Track your promotion performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-gray-400 text-sm">Last 7 days</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <Card key={i} className="bg-void-paper border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  stat.color === 'mint' ? 'bg-neon-mint/10' :
                  stat.color === 'violet' ? 'bg-neon-violet/10' : 'bg-neon-amber/10'
                }`}>
                  <stat.icon className={`w-5 h-5 ${
                    stat.color === 'mint' ? 'text-neon-mint' :
                    stat.color === 'violet' ? 'text-neon-violet' : 'text-neon-amber'
                  }`} />
                </div>
                {stat.trend && (
                  <div className={`flex items-center gap-1 text-xs ${
                    stat.trend === 'up' ? 'text-neon-mint' : 'text-red-400'
                  }`}>
                    {stat.trend === 'up' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {stat.trend === 'up' ? 'Good' : 'Low'}
                  </div>
                )}
              </div>
              <p className="font-mono font-bold text-3xl text-white mb-1">{stat.value}</p>
              <p className="text-gray-500 text-sm">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Views Over Time */}
        <Card className="lg:col-span-2 bg-void-paper border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-heading">Views Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={overview?.daily_stats || []}>
                  <defs>
                    <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E599" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00E599" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    stroke="#525252" 
                    fontSize={12}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                  />
                  <YAxis stroke="#525252" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0A0A0A', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#00E599" 
                    strokeWidth={2}
                    fill="url(#viewsGrad)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Views by Promotion */}
        <Card className="bg-void-paper border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-heading">Views by Promotion</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0A0A0A', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-gray-500">No data yet</p>
              </div>
            )}
            <div className="space-y-2 mt-4">
              {pieData.slice(0, 4).map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-gray-400 text-sm truncate max-w-[120px]">{item.name}</span>
                  </div>
                  <span className="font-mono text-white text-sm">{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promotion-Specific Analytics */}
      <Card className="bg-void-paper border-white/10">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-white font-heading">Promotion Performance</CardTitle>
            <Select value={selectedPromo || 'all'} onValueChange={(val) => handlePromoSelect(val === 'all' ? null : val)}>
              <SelectTrigger className="w-64 bg-void border-white/10">
                <SelectValue placeholder="Select a promotion" />
              </SelectTrigger>
              <SelectContent className="bg-void-paper border-white/10">
                <SelectItem value="all" className="text-gray-400">All Promotions</SelectItem>
                {promotions.map(promo => (
                  <SelectItem key={promo.id} value={promo.id} className="text-white">
                    {promo.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {promoAnalytics ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-void rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">Views</p>
                  <p className="font-mono font-bold text-2xl text-white">
                    {promoAnalytics.promotion.views}
                  </p>
                </div>
                <div className="bg-void rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">Clicks</p>
                  <p className="font-mono font-bold text-2xl text-white">
                    {promoAnalytics.promotion.clicks}
                  </p>
                </div>
                <div className="bg-void rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">CTR</p>
                  <p className="font-mono font-bold text-2xl text-neon-mint">
                    {promoAnalytics.ctr}%
                  </p>
                </div>
                <div className="bg-void rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">Cost/View</p>
                  <p className="font-mono font-bold text-2xl text-white">
                    {promoAnalytics.cost_per_view}
                  </p>
                </div>
              </div>
              
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={promoAnalytics.daily_views}>
                    <XAxis 
                      dataKey="date" 
                      stroke="#525252" 
                      fontSize={12}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                    />
                    <YAxis stroke="#525252" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0A0A0A', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="views" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Select a promotion to view detailed analytics</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Promotions Table */}
      <Card className="bg-void-paper border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-heading">All Promotions Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 text-sm border-b border-white/10">
                  <th className="pb-3 font-medium">Promotion</th>
                  <th className="pb-3 font-medium">Category</th>
                  <th className="pb-3 font-medium">Views</th>
                  <th className="pb-3 font-medium">Clicks</th>
                  <th className="pb-3 font-medium">CTR</th>
                  <th className="pb-3 font-medium">Spent/Budget</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((promo) => {
                  const ctr = promo.views > 0 ? (promo.clicks / promo.views * 100).toFixed(1) : 0;
                  return (
                    <tr key={promo.id} className="border-b border-white/5">
                      <td className="py-4">
                        <p className="text-white font-medium truncate max-w-[200px]">{promo.title}</p>
                      </td>
                      <td className="py-4">
                        <span className="px-2 py-1 bg-white/5 rounded text-gray-400 text-sm">
                          {promo.category}
                        </span>
                      </td>
                      <td className="py-4 font-mono text-white">{promo.views}</td>
                      <td className="py-4 font-mono text-white">{promo.clicks}</td>
                      <td className="py-4 font-mono text-neon-mint">{ctr}%</td>
                      <td className="py-4">
                        <span className="font-mono text-white">{promo.credits_spent}</span>
                        <span className="text-gray-500">/{promo.credit_budget}</span>
                      </td>
                    </tr>
                  );
                })}
                {promotions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      No promotions yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
